import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, handleChunkError } from '@/utils/chunkRetry';
import { ErrorFallback } from '@/components/ErrorFallback';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Lightweight error boundary for individual routes.
 * Catches errors within a single page so the rest of the app stays functional.
 * The top-level ErrorBoundary in App.tsx remains as a last resort.
 */
export default class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error) && handleChunkError()) return;
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
      tags: { boundary: 'route' },
    });
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/home';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem 1.5rem',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'var(--danger, #ef4444)',
              opacity: 0.15,
              marginBottom: '1rem',
            }}
          />
          <ErrorFallback
            title="This page encountered an error"
            message="Something went wrong loading this page. You can try again or head back to the home screen."
            actions={[
              { label: 'Try Again', onClick: this.handleRetry, variant: 'secondary' },
              { label: 'Go Home', onClick: this.handleGoHome, variant: 'primary' },
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
